// @flow
import React from 'react'
import Snackbar from '@material-ui/core/Snackbar'
import Map from 'catena/er2_map/src/map'
import type { ExtentType, LayerAnimationType } from 'catena/er2_map/src/map'
import type { MapsourcesType } from 'catena/er2_map_userlayers/js/reducers/mapsources'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import copyclipboard from 'copy-to-clipboard'
import OlGeomPolygon from 'ol/geom/Polygon'
import OlFeature from 'ol/Feature'
import OlMap from 'ol/Map'
import VectorLayer from 'ol/layer/Vector'
import OlStyleFill from 'ol/style/Fill'
import OlStyle from 'ol/style/Style'
import OlStyleStroke from 'ol/style/Stroke'
import VectorSource from 'ol/source/Vector'
import OlInteractionSelect from 'ol/interaction/Select'
import OlStyleText from 'ol/style/Text'
import GeoJSON from 'ol/format/GeoJSON'
import * as OlCondition from 'ol/events/condition'
import * as d3 from 'd3-scale'
import ChartPanel from './chart'
import type { CollaborationType, SingleClick } from '../reducers/collaboration'
import type { CollaborationSettingsType } from '../reducers/collaboration_settings'
import type { FicusDataType, TransimDataType } from '../reducers/select_framework_bar'
import stylesFicus from '../styles'
import type { AnalysisPanelType, LocationType } from '../reducers/analysis_panel'
import type { SettingsBarType } from '../reducers/settings'
import type { AnalyzeFrameworkBarType } from '../reducers/analyze_framework_bar'
import type { ViewType } from '../reducers/map'
import * as colors from '../styles/colors'

type Props = {
    analysisPanel: AnalysisPanelType,
    baseLayer: string,
    collaboration: CollaborationType,
    collaborationSettings: CollaborationSettingsType,
    extent: ExtentType,
    ficusData: FicusDataType,
    ficusNode: string,
    ficusControlData: AnalyzeFrameworkBarType,
    // If true, then process single clicks
    frameworksActive: boolean,
    handledClickMap: Function,
    handledClickRealizations: Function,
    hasAnalysisPanel: Function,
    hasHeader: boolean,
    lookupPoint: Function,
    mapScale: number,
    mapsources: MapsourcesType,
    onClickRealizations: Function,
    onSingleClick: Function,
    removeMapLocation: Function,
    setAnalysisData: Function,
    setAnalyzeMode: Function,
    setFicusConfig: Function,
    setFicusData: Function,
    setMapApp: Function,
    setTransimConfig: Function,
    settings: SettingsBarType,
    updateCurrentView: Function,
    updateMapLocation: Function,
    theme: stylesFicus,
    tocVisible: boolean,
}

type State = {
    // If true, update extent on layer change
    canUpdateExtent: boolean,
    // List of current locations displayed in the map. Key is point title, value is placemark ID.
    mapLocations: LocationType[],
    message: string,
    pmIds: string[],
}

const frameNameTypes = {
    '1q': '1st Quartile',
    min: 'Min',
    median: 'Median',
    '3q': '3rd Quartile',
    max: 'Max',
}

function locationsAreEqual(loc1: [number, number], loc2: [number, number]) {
    let ret = true
    loc1.forEach((l, i) => {
        if (Math.abs(loc1[i] - loc2[i]) > 0.00005) ret = false
    })
    return ret
}

class FicusMap extends React.Component<Props, State> {
    static defaultProps = {
        updateCurrentView: (ex: ViewType) => ex,
        userLayers: [],
    }
    mapApp: any

    constructor(props: Props) {
        super(props)
        this.state = {
            canUpdateExtent: true,
            mapLocations: [],
            message: '',
            pmIds: [],
            // View if set by collaboration state
            view: null,
        }

        // Cache vectors
        this.vectorLayers = {}
    }

    componentDidMount() {
        this.processTransimData()

        // Add layer to display the current calculated extent.
        const vectorSource = new VectorSource()
        this.vectorExtent = new VectorLayer({ source: vectorSource })
        this.mapApp.map.olmap.addLayer(this.vectorExtent)
    }

    componentDidUpdate(prevProps: Props, prevState: State, prevContent: any) {
        this.processLocationData()
        this.processTransimData(prevProps)
    }

    componentWillReceiveProps(newProps: Props) {
        if (newProps.settings.popupOpacity !== this.props.settings.popupOpacity) {
            // Update placemarks
            this.state.pmIds.forEach((pmId) => {
                this.updatePlacemarkStyle(pmId)
            })
        }

        if (newProps.collaboration.enabled) {
            if (newProps.collaboration.sharedState.propagateMapView && newProps.collaboration.sharedState.state.mapView &&
                this.state.view !== newProps.collaboration.sharedState.state.mapView) {
                if (newProps.collaborationSettings.boundingBox) {
                    const view = newProps.collaboration.sharedState.state.mapView
                    // Save the view so we don't process it endlessly
                    this.setState({ view })
                    newProps.updateMapView(view)

                    // Use this to test what openlayers tried to fit the extent to.
                    const showExtent = false
                    if (showExtent) {
                        const polygon = OlGeomPolygon.fromExtent(view.extent)
                        const feature = new OlFeature(polygon)
                        this.vectorExtent.getSource().clear()
                        this.vectorExtent.getSource().addFeature(feature)

                        const olmap = this.mapApp.map.olmap
                        olmap.getView().fit(view.extent, {
                            callback: (e) => {
                                const mapview = olmap.getView()
                                const newpolygon = OlGeomPolygon.fromExtent(this.mapApp.map.getExtent())
                                const newfeature = new OlFeature(newpolygon)
                                this.vectorExtent.getSource().addFeature(newfeature)
                            },
                        })
                    }
                }
            }
        }

        // Check for invalid map locations
        newProps.analysisPanel.mapLocations.forEach((location) => {
            // Locations stats are set to undefined if they are still being fetched
            const hasData = location.stats === undefined || Object.keys(location.stats).length > 0
            if (!hasData) {
                this.props.removeMapLocation(location)
                if (!this.state.message) {
                    this.addMessage('Sorry, no data for this location')
                }
            }

            // Check for realizations click
            if (this.props.collaboration.sharedState.state.realizationsClick) {
                if (this.props.collaborationSettings.analyzeMode) {
                    if (locationsAreEqual(location.lonlat, this.props.collaboration.sharedState.state.realizationsClick.lonlat)) {
                        this.updateChart(location)
                    }
                    // Notify that we have handled the realization chart request
                    this.props.handledClickRealizations()
                }
            }
        })
    }

    addMessage(message: string) {
        this.setState({ ...this.state, message })
    }

    removeMessage() {
        this.setState({ ...this.state, message: '' })
    }

    buildTransimLayer() {
        const transimData = this.props.ficusData.themes.transims[this.props.ficusControlData.iframe]
        if (transimData) {
            const { iInterval, showLabels } = this.props.ficusControlData
            const transimAttrs = transimData.transimAttrs
            const meta = this.props.ficusData.themes.meta
            const attr = transimAttrs[iInterval]
            const geoJSON = new GeoJSON()
            const vals = transimData.attrs.map(a => a.properties[attr])
            const min = Math.min(...vals)
            const max = Math.max(...vals)
            const range = meta.classification || ['red', 'yellow', 'green']
            const colorScale = d3.scaleLinear().domain([min, (max - min) / 2, max]).range(range)

            const styleFunction = (f, resolution) => {
                let width = 3
                const val = f.get(attr)
                let color = colorScale(val || 0)
                // console.log(`${val} has color ${color[0]}`)
                if (transimData.schema.properties.fspd_ab && transimData.filepath.toLowerCase().indexOf('speed') > 0) {
                    // This is a speed limit, so take that value as max
                    let thisMax = f.get('fspd_ab')
                    if (thisMax < val) thisMax = val
                    const thisColorScale = d3.scaleLinear().domain([min, (thisMax - min) / 2, thisMax]).range(range)
                    color = thisColorScale(val)
                }
                let textStyle
                if (val === null) {
                    width = 0.00001
                } else if (resolution < 25 && showLabels) {
                    textStyle = new OlStyleText({
                        backgroundFill: new OlStyleFill({
                            color: 'white',
                        }),
                        color: 'black',
                        text: `${val}`,
                    })
                }
                f.set('_width', width)
                return [
                    new OlStyle({
                        stroke: new OlStyleStroke({
                            color,
                            width,
                        }),
                        text: textStyle,
                    }),
                ]
            }

            const olmap: OlMap = this.mapApp.map.olmap
            // Look for cached layer
            let vectorLayer = this.vectorLayers[transimData.filepath]
            Object.entries(this.vectorLayers).forEach(([key, value]) => {
                if (value !== vectorLayer) value.setVisible(false)
            })

            if (!vectorLayer) {
                const features = transimData.attrs.map((f => geoJSON.readFeature(f)))
                const vectorSource = new VectorSource({ features })
                vectorLayer = new VectorLayer({ source: vectorSource, style: styleFunction, declutter: true, name: transimData.filepath })
                olmap.addLayer(vectorLayer)
                if (this.state.canUpdateExtent) {
                    olmap.getView().fit(vectorSource.getExtent(), { size: olmap.getSize() })
                }
                this.setState({ ...this.state, canUpdateExtent: false })
                this.vectorLayers[transimData.filepath] = vectorLayer

                // Feature hover
                const selectPointerMove = new OlInteractionSelect({
                    condition: OlCondition.pointerMove,
                })
                selectPointerMove.on('select', (e) => {
                    e.deselected.forEach(f => f.setStyle(null))
                    e.selected.forEach((f) => {
                        f.setStyle(
                            new OlStyle({
                                stroke: new OlStyleStroke({
                                    color: 'pink',
                                    width: f.get('_width'),
                                }),
                            }),
                        )
                    })
                })
                // olmap.addInteraction(selectPointerMove)
            } else {
                vectorLayer.setVisible(true)
                vectorLayer.setStyle(styleFunction)
                vectorLayer.changed()
                vectorLayer.setOpacity(this.props.settings.opacity)
            }
        }
    }

    /**
     * Manage map popups
     */
    processLocationData() {
        const popupDim = {
            width: 150,
            height: 200,
            minimal: true,
            marginBottom: 10,
            spacingBottom: 10,
            spacingTop: 0,
            marginTop: 10,
        }

        const copybutton = (
            <CopyToClipboard
                text={'No statistics'}
            >
                <button className={`${this.props.theme.detailsButton} ${this.props.theme.marginLeftButton}`} data-button="copy">
                    Copy
                </button>
            </CopyToClipboard>
        )

        const getChart = (location: LocationType) => {
            if (location.stats) {
                return (
                    <div>
                        <ChartPanel
                            type={'boxplot'}
                            minimal
                            chartStyle={{ ...popupDim, height: 200, width: 113 }}
                            chartData={location}
                        />
                        {location.stats.max <= 1 && (
                            <ChartPanel
                                type={'bellcurve'}
                                minimal
                                chartStyle={{ ...popupDim, height: 200, width: 56 }}
                                chartData={location}
                            />
                        )}
                        <div className={this.props.theme.details}>
                            {location.values.length > 0 && (
                                <button className={this.props.theme.detailsButton} data-button="updatechart">
                                    Realizations
                                </button>
                            )}
                            {copybutton}
                        </div>
                    </div>
                )
            }
            return (
                <div style={{ width: 250, height: 250 }}>
                    <img
                        alt="Loading..."
                        src="/static/er2_ficus/img/dualring.gif"
                        style={{ marginTop: '25%', marginLeft: '40%', height: '25%' }}
                    />
                </div>
            )
        }

        const getContent = (location: LocationType) => (
            <span className={this.props.theme.content}>
                <div className={this.props.theme.title}>{`Location ${location.title}`}
                    <button className={this.props.theme.closeButton}>
                        <i
                            className={`fa fa-close popupcloser ${this.props.theme.closeButton}`}
                        />
                    </button>
                </div>
                <div style={{ textAlign: 'center', fontSize: '12px', marginTop: '16px' }}>
                    {location && location.stats && Object.keys(location.stats).length > 0 ? (
                        <span>Std Dev: <b>{location.stats.sd}</b>, Mean: <b>{location.stats.mean}</b></span>) : ''}
                </div>
                {getChart(location)}
                <span className={'pulse'} />
            </span>
        )

        const getCopyData = (location: LocationType) => {
            let ret = ''
            const cr = String.fromCharCode(13)
            const stats = [['min', 'Minimum'], ['1q', '1st Quartile'], ['median', 'Median'], ['mean', 'Mean'],
                ['3q', '3rd Quartile'], ['max', 'Max'], ['range', 'Range'], ['sd', 'Standard Deviation']]
            if (location.lonlat) ret += `Latitude:\t${location.lonlat[1]}${cr}Longitude:\t${location.lonlat[0]}${cr}`
            stats.forEach((s) => {
                if (location.stats[s[0]]) ret += `${s[1]}:\t${location.stats[s[0]]}${cr}`
            })
            if (location.values.length) ret += `Realizations:\t${location.values.map(v => v[0]).join(',')}${cr}`
            return ret
        }

        this.props.analysisPanel.mapLocations.forEach((location) => {
            const buttonEventsByClass = {
                updatechart: (nopropagate: boolean) => {
                    this.updateChart(location)
                    this.props.onClickRealizations(location.lonlat)
                },
                copy: () => {
                    copyclipboard(getCopyData(location))
                    this.addMessage('Cell statistics copied to clipboard')
                },
                popupcloser: (popup) => {
                    const pmId = popup.id
                    const idx = this.state.pmIds.indexOf(pmId)
                    const loc = this.state.mapLocations[idx]
                    this.props.removeMapLocation(loc)
                    popup.remove()
                },
            }

            // Check for new locations
            const idx = this.state.mapLocations.findIndex(l => l.lonlat === location.lonlat)
            if (idx < 0) {
                // Not in local cache, so add it to the map.
                const pmId = this.mapApp.addPlacemark(location.coordinate, getContent(location), buttonEventsByClass)
                this.state.mapLocations.push(location)
                this.state.pmIds.push(pmId)
                this.updatePlacemarkStyle(pmId)
            } else if (!this.state.mapLocations[idx].values || this.state.mapLocations[idx].values.length === 0) {
                // Placemark update if the cell values have been set.
                const content = getContent(location)
                const pmId = this.state.pmIds[idx]
                this.mapApp.updatePlacemark(pmId, content, buttonEventsByClass)
                this.state.mapLocations[idx] = location
            }
        })

        // check for locations that are in the cache but no longer exist
        const toRemove = []
        this.state.mapLocations.forEach((location, i) => {
            const locationState = this.props.analysisPanel.mapLocations.find((l: LocationType) => l.title === location.title)
            if (!locationState) {
                // Doesn't exists in props, so remove it.
                const pmId = this.state.pmIds[i]
                this.mapApp.removePlacemark(pmId)
                toRemove.push(i)
            }
        })
        toRemove.forEach((i) => {
            this.state.pmIds.splice(i, 1)
            this.state.mapLocations.splice(i, 1)
        })

        // Add halo to selected popup
        this.state.mapLocations.forEach((location, i) => {
            const pmId = this.state.pmIds[i]
            const el = document.getElementById(pmId)
            if (el && this.props.analysisPanel.analysisLocation) {
                if (location.lonlat === this.props.analysisPanel.analysisLocation.lonlat) {
                    el.style.boxShadow = `0 0 3px 3px ${colors.blueDarken5}`
                } else {
                    el.style.boxShadow = ''
                }
            }
        })
    }

    processTransimData(props: Props) {
        if (this.props.ficusData.themes.transims) {
            if (this.props.ficusControlData.started) {
                // Look for animation. Raster themes are handled by er2_map.
                if (!props || !props.ficusControlData.started) {
                    // Was stopped, now started
                    const that = this
                    let { iframe, iInterval } = props.ficusControlData
                    const run = () => {
                        function mod(n, m) {
                            return ((n % m) + m) % m
                        }
                        if (that.props.ficusData.themes.transims) {
                            if (props.ficusControlData.animationType === 'frame') {
                                iframe = mod(iframe + 1, props.ficusData.themes.transims.length)
                            } else if (props.ficusControlData.animationType === 'interval') {
                                iInterval = mod(iInterval + 1, props.ficusData.intervals.length)
                            }
                            that.props.setFicusConfig({
                                iframe, iInterval,
                            })
                            if (that.props.ficusControlData.started) {
                                that.timer = setTimeout(run, that.props.ficusControlData.speedFrame)
                            } else {
                                that.timer = null
                            }
                        } else {
                            that.timer = null
                        }
                    }
                    if (!this.timer) {
                        this.timer = setTimeout(run, this.props.ficusControlData.speedFrame)
                    }
                }
            }
            this.buildTransimLayer()
        } else if (this.state.vectorLayer) {
            this.mapApp.map.olmap.removeLayer(this.state.vectorLayer)
            this.setState({ ...this.state, vectorLayer: null })
        }
    }
    updatePlacemarkStyle(pmId: string) {
        const pmDiv = document.getElementById(pmId)
        if (pmDiv) {
            const bg = `rgba(255,255,255,${this.props.settings.popupOpacity})`
            pmDiv.style.backgroundColor = bg
            pmDiv.onmouseover = function () {
                this.style.backgroundColor = 'rgba(255,255,255,1)'
            }
            pmDiv.onmouseleave = function () {
                this.style.backgroundColor = bg
            }
        }
    }

    updateChart(location: LocationType) {
        // Switch to realization data.
        this.props.setAnalysisData(location)
        this.props.setAnalyzeMode('_allRealizations')
    }

    render() {
        const onSingleClick = (e, mapObj) => {
            if (this.props.ficusData && this.props.onSingleClick && this.props.frameworksActive) {
                const interval = this.props.ficusData.intervals[this.props.ficusControlData.iInterval]
                this.props.lookupPoint(e.coordinate, e.lonlat, this.props.ficusNode, interval)

                this.props.onSingleClick({
                    coordinate: e.coordinate,
                    lonlat: e.lonlat,
                    originalEvent: {
                        clientX: e.originalEvent.clientX,
                        clientY: e.originalEvent.clientY,
                    },
                })
            }
        }

        const onFrameChange = (iframe, iInterval) => {
            this.props.setFicusConfig({ iframe, iInterval })
        }
        const onChangeView = (ex) => {
            this.props.updateCurrentView(ex)
            if (this.props.ficusControlData.started) {
                // Restart animation from first frame if in realizations mode
                if (this.props.ficusControlData.mode === '_allRealizations') {
                    // Pause animation so that the map doesn't get out of sync.
                    this.props.setFicusConfig({ iframe: 0, started: false })
                } else {
                    this.props.setFicusConfig({ started: false })
                }
                // Put in a delay so that the map has a chance to stop any caching
                setTimeout(() => this.props.setFicusConfig({ started: true }), this.props.ficusControlData.speedFrame)
            }
        }

        let layerAnimationData: LayerAnimationType
        const hasRasterAnimationData = this.props.ficusData.themeType === 'raster'
        const hasTransimAnimationData = this.props.ficusData.themeType === 'vector'
        if (hasRasterAnimationData) {
            layerAnimationData = {
                themes: this.props.ficusData.themes,
                config: {
                    ...this.props.ficusControlData,
                    opacity: this.props.settings.opacity,
                    animationMode: this.props.settings.animationMode,
                },
                frameChanged: onFrameChange,
            }

            // Override the iteration order if we are not in AllRealizations mode and we are in raster mode
            if (this.props.ficusControlData.mode !== '_allRealizations' && this.props.ficusData.themes[this.props.ficusControlData.iInterval].mapfiles) {
                layerAnimationData.config.iterationOrder = this.props.ficusData.themes[this.props.ficusControlData.iInterval].mapfiles.map((m, i: number) => i)
            }
        }

        let frameName
        const iframe = this.props.ficusControlData.iframe
        if (hasRasterAnimationData) {
            const order = this.props.ficusControlData.iterationOrder
            const sortedIframe = order.length ? order[iframe] : iframe
            // If the mode changes, the frame number may be out of range
            if (sortedIframe < this.props.ficusData.themes[this.props.ficusControlData.iInterval].mapfiles.length) {
                frameName = this.props.ficusData.themes[this.props.ficusControlData.iInterval].mapfiles[sortedIframe].layer
                const s1 = frameName.lastIndexOf('_')
                const s2 = frameName.lastIndexOf('.')
                frameName = frameName.substring(s1 + 1, s2)
                frameName = frameNameTypes[frameName] || frameName

                // Hack to remove rzn from frame name
                if (frameName.substr(0, 3) === 'rzn') frameName = frameName.substr(3)
            }
        } else if (hasTransimAnimationData) {
            // Check if iframe is still good -- this can happen when animating
            if (this.props.ficusData.themes.transims[iframe]) {
                const filepath = this.props.ficusData.themes.transims[iframe].filepath
                // Realization is the parent of the transim shapefile
                const reRealization = /.*\/_.+\/(.+)\/.*/
                // Just match the number at the end
                const match = filepath.match(reRealization)
                frameName = match[1]
            }
        }
        if (frameName) {
            // Add interval if present
            const interval = this.props.ficusData.intervals[this.props.ficusControlData.iInterval]
            if (interval) frameName += ` (${interval})`
        }

        // Set the map view to match what has been shared, if sharing is enabled
        let click
        if (this.props.collaboration.enabled) {
            if (this.props.collaboration.sharedState.state.mapClick) {
                if (this.props.collaborationSettings.locations) {
                    click = this.props.collaboration.sharedState.state.mapClick
                }
            }
        }

        const classNames = [this.props.theme.mapCtr, this.props.theme.mapToolbarPanelClosed]

        // detect IE8 and above, and edge
        if (document.documentMode || /Edge/.test(navigator.userAgent)) {
            console.warn('Disabling map overlap of analysis panel for edge')
            if (!this.props.hasAnalysisPanel) classNames.push(styles.moveDown)
        } else {
            classNames.push(this.props.hasAnalysisPanel ? this.props.theme.analysisPanelOpen : this.props.theme.analysisPanelClosed)
        }
        classNames.push(this.props.tocVisible ? this.props.theme.contextBarOpen : this.props.theme.contextBarClosed)
        classNames.push(this.props.hasHeader ? this.props.theme.headerPanelOpen : this.props.theme.headerPanelClosed)

        return (
            <div
                style={{ position: 'relative' }}
                className={classNames.join(' ')}
            >
                <Map
                    {...this.props}
                    click={click}
                    layerAnimationData={layerAnimationData}
                    onChangeView={onChangeView}
                    onSingleClick={onSingleClick}
                    ref={(mapApp) => {
                        this.mapApp = mapApp
                    }}
                    theme={this.props.theme}
                />
                <div className={this.props.theme.frameContainer}>
                    <div>
                        <span className={this.props.theme.frameName} style={{ backgroundColor: `rgba(255, 255, 255, ${this.props.settings.popupOpacity})` }}>{frameName}</span>
                    </div>
                </div>
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    autoHideDuration={4000}
                    message={this.state.message}
                    open={this.state.message !== ''}
                    onClose={() => this.removeMessage()}
                    ContentProps={{ style: { backgroundColor: colors.secondaryOne } }}
                />
            </div>
        )
    }
}

export default FicusMap
